/**
 * Ask Titan Panel
 *
 * An always-available floating chat panel for the intelligence query agent.
 * A fixed launcher button (bottom-left, to avoid the bottom-right BugReportWidget)
 * opens a glass-cockpit chat panel. Messages stream through the askTitanStore,
 * which calls POST /api/intelligence/agent. Assistant replies render the tools
 * the agent used as small muted "sources" chips.
 */

import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, X, Send, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Alert, AlertDescription } from './ui/alert';
import { useAskTitanStore } from '../stores/askTitanStore';

const SUGGESTED_PROMPTS = [
  'How are we doing this week, net of fees?',
  'Which strategies are bleeding?',
  "What's currently defunded or down-weighted?",
];

const AskTitanPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const messages = useAskTitanStore((s) => s.messages);
  const loading = useAskTitanStore((s) => s.loading);
  const activeTool = useAskTitanStore((s) => s.activeTool);
  const error = useAskTitanStore((s) => s.error);
  const sendMessage = useAskTitanStore((s) => s.sendMessage);
  const clearMessages = useAskTitanStore((s) => s.clearMessages);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the conversation pinned to the latest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = (text: string) => {
    if (loading) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    void sendMessage(trimmed);
    setDraft('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(draft);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center group"
        title="Ask Titan"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="absolute -top-8 left-0 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Ask Titan
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 z-50 w-96 max-w-[calc(100vw-3rem)] flex flex-col bg-card text-card-foreground border rounded-lg shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">Ask Titan</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={clearMessages}
              className="text-muted-foreground hover:text-foreground p-1"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setIsOpen(false)}
            className="text-muted-foreground hover:text-foreground p-1"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="h-96">
        <div ref={scrollRef} className="h-96 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Ask about performance, strategies, risk, or what the platform is doing.
              </p>
              <div className="space-y-2">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => handleSend(prompt)}
                    className="w-full text-left text-xs px-3 py-2 rounded-md border bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'
                }`}
              >
                {/* A streaming assistant message may be empty initially: show a
                    typing indicator (optionally naming the tool in flight). */}
                {msg.role === 'assistant' && msg.content === '' && loading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {activeTool ? `using ${activeTool}…` : 'Thinking...'}
                  </div>
                ) : (
                  <div>{msg.content}</div>
                )}
                {msg.role === 'assistant' && msg.toolsUsed && msg.toolsUsed.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {msg.toolsUsed.map((tool) => (
                      <span
                        key={tool}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-background/60 text-muted-foreground border"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* The typing indicator now lives inside the streaming assistant
              bubble (above), so no separate "Thinking..." block is needed. */}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </ScrollArea>

      {/* Composer */}
      <div className="flex items-center gap-2 p-3 border-t">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Titan anything..."
          disabled={loading}
          className="h-9 text-sm"
        />
        <Button
          onClick={() => handleSend(draft)}
          disabled={loading || !draft.trim()}
          size="icon"
          className="h-9 w-9 shrink-0"
          title="Send"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default AskTitanPanel;
